import { useState } from "react";

import { PRO014_BULK_ACTION } from "@/@types/campaign";
import type {
  IBulkUpdatePro014ItemsResponse,
  IPro014CampaignItem,
  IPro014ItemsPage,
  Pro014BulkAction,
} from "@/@types/campaign";
import type { CampaignConflictItem } from "@/@types/campaign-conflict";
import { useToast } from "@/components/ui/use-toast";
import {
  useActivatePro014CampaignItemMutation,
  useBulkUpdatePro014ItemsMutation,
  useDeactivatePro014CampaignItemMutation,
} from "@/services/campaign-pro014";

import { TOAST_VARIANT } from "../../utils/messages";
import { conflictsFromError, describeApiError } from "../../utils/pro014-api-errors";
import { PRODUCTS_TEXTS } from "../constants";
import type { ProductsActionsState, SelectedProduct } from "../types";

const toSelected = (item: SelectedProduct): SelectedProduct => ({
  itemCode: item.itemCode,
  storeCode: item.storeCode,
});

const sameProduct = (first: SelectedProduct, second: SelectedProduct): boolean =>
  first.itemCode === second.itemCode && first.storeCode === second.storeCode;

export function useProductsActions(campaignCode: number): ProductsActionsState {
  const { toast } = useToast();
  const [selectedItems, setSelectedItems] = useState<SelectedProduct[]>([]);
  const [bulkAction, setBulkAction] = useState<Pro014BulkAction | null>(null);
  const [conflicts, setConflicts] = useState<CampaignConflictItem[]>([]);
  const [bulkUpdate, { isLoading: isBulkLoading }] = useBulkUpdatePro014ItemsMutation();
  const [activateItem, { isLoading: isActivating }] = useActivatePro014CampaignItemMutation();
  const [deactivateItem, { isLoading: isDeactivating }] = useDeactivatePro014CampaignItemMutation();

  const toggleMutations: Record<Pro014BulkAction, typeof activateItem> = {
    [PRO014_BULK_ACTION.ACTIVATE]: activateItem,
    [PRO014_BULK_ACTION.DEACTIVATE]: deactivateItem,
  };

  const toggleToasts: Record<Pro014BulkAction, (item: IPro014CampaignItem) => { title: string; description: string }> = {
    [PRO014_BULK_ACTION.ACTIVATE]: (item) => ({
      title: PRODUCTS_TEXTS.ITEM_ACTIVATED_TITLE,
      description: PRODUCTS_TEXTS.ITEM_ACTIVATED(item.itemCode, item.storeCode),
    }),
    [PRO014_BULK_ACTION.DEACTIVATE]: (item) => ({
      title: PRODUCTS_TEXTS.ITEM_DEACTIVATED_TITLE,
      description: PRODUCTS_TEXTS.ITEM_DEACTIVATED(item.itemCode, item.storeCode),
    }),
  };

  const reportFailure = (error: unknown, fallback: string): void => {
    const found: CampaignConflictItem[] = conflictsFromError(error);
    if (found.length > 0) {
      setConflicts(found);
      return;
    }
    toast({
      title: PRODUCTS_TEXTS.ERROR,
      description: describeApiError(error, fallback),
      variant: TOAST_VARIANT.DESTRUCTIVE,
    });
  };

  const isSelected = (item: IPro014CampaignItem): boolean =>
    selectedItems.some((selected: SelectedProduct) => sameProduct(selected, item));

  const toggleSelection = (item: IPro014CampaignItem): void =>
    setSelectedItems((previous: SelectedProduct[]) => {
      if (previous.some((selected) => sameProduct(selected, item))) {
        return previous.filter((selected) => !sameProduct(selected, item));
      }
      return [...previous, toSelected(item)];
    });

  const toggleAll = (page: IPro014ItemsPage | undefined): void => {
    const items: IPro014CampaignItem[] = page?.items ?? [];
    if (selectedItems.length === items.length) {
      setSelectedItems([]);
      return;
    }
    setSelectedItems(items.map(toSelected));
  };

  const requestBulkAction = (action: Pro014BulkAction): void => {
    if (selectedItems.length === 0) {
      toast({
        title: PRODUCTS_TEXTS.ATTENTION,
        description: PRODUCTS_TEXTS.SELECT_AT_LEAST_ONE,
        variant: TOAST_VARIANT.DESTRUCTIVE,
      });
      return;
    }
    setBulkAction(action);
  };

  const confirmBulkAction = async (): Promise<void> => {
    if (!bulkAction) return;
    try {
      const result: IBulkUpdatePro014ItemsResponse = await bulkUpdate({
        code: campaignCode,
        items: selectedItems,
        action: bulkAction,
      }).unwrap();
      toast({ title: PRODUCTS_TEXTS.SUCCESS, description: result.message, variant: TOAST_VARIANT.DEFAULT });
      setSelectedItems([]);
    } catch (error: unknown) {
      reportFailure(error, PRODUCTS_TEXTS.BULK_FAILED);
    } finally {
      setBulkAction(null);
    }
  };

  const toggleItem = async (item: IPro014CampaignItem): Promise<void> => {
    if (!item.storeCode) {
      toast({
        title: PRODUCTS_TEXTS.ERROR,
        description: PRODUCTS_TEXTS.INVALID_STORE(item.storeCode),
        variant: TOAST_VARIANT.DESTRUCTIVE,
      });
      return;
    }
    const action: Pro014BulkAction = item.isActive
      ? PRO014_BULK_ACTION.DEACTIVATE
      : PRO014_BULK_ACTION.ACTIVATE;
    try {
      await toggleMutations[action]({
        code: campaignCode,
        itemCode: item.itemCode,
        storeCode: item.storeCode,
      }).unwrap();
      toast(toggleToasts[action](item));
    } catch (error: unknown) {
      reportFailure(error, PRODUCTS_TEXTS.TOGGLE_FAILED);
    }
  };

  return {
    selectedItems,
    bulkAction,
    isBulkLoading,
    isToggling: isActivating || isDeactivating,
    conflicts,
    isSelected,
    toggleSelection,
    toggleAll,
    requestBulkAction,
    cancelBulkAction: () => setBulkAction(null),
    confirmBulkAction,
    toggleItem,
    closeConflicts: () => setConflicts([]),
  };
}
